import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/User';
import { getCurrentUser, login as loginService, register as registerService, logout as logoutService, isAuthenticated } from '../services/authService';

/*
 * Google sign-in serves both new registrations and returning users, so the
 * caller needs to know which happened — firing a CompleteRegistration event
 * on every sign-in would count every returning user as a new signup.
 *
 * `isNewUser` requires the backend to report it from POST /auth/google. It
 * is read from `data.isNewUser` or `data.data.isNewUser`, and defaults to
 * false when absent, so a backend that doesn't send it simply undercounts
 * rather than inventing conversions.
 */
export interface GoogleLoginResult {
  success: boolean;
  isNewUser: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  googleLogin: (code: string) => Promise<GoogleLoginResult>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isAuthenticated());

  // Load user on initial render if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (isAuthenticated()) {
        try {
          setLoading(true);
          const response = await getCurrentUser();
          if (response.success && response.data) {
            setUser(response.data);
            setIsLoggedIn(true);
          } else {
            // Token invalid or expired
            logoutService();
            setIsLoggedIn(false);
          }
        } catch (err) {
          setError('Failed to load user');
          logoutService();
          setIsLoggedIn(false);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const googleLogin = async (code: string): Promise<GoogleLoginResult> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      // Only succeed if backend says success === true
      if (response.ok && data.success && data.data?.user) {
        setUser(data.data.user);
        setIsLoggedIn(true);

        if (data.data.token) {
          localStorage.setItem('token', data.data.token);
        }

        const newUserFlag = data.data?.isNewUser ?? data.isNewUser;
        if (newUserFlag === undefined) {
          // Discoverable rather than silent: without this the signup
          // conversion is simply never reported for Google accounts.
          console.info('[auth] /auth/google did not return isNewUser — Google signups will not be counted as registrations');
        }

        return { success: true, isNewUser: newUserFlag === true };
      } else {
        setError(data.error || 'Google login failed');
        return { success: false, isNewUser: false };
      }
    } catch (err) {
      setError('Google login failed');
      return { success: false, isNewUser: false };
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await loginService({ email, password });
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoggedIn(true);
        return true;
      } else {
        setError(response.error || 'Login failed');
        return false;
      }
    } catch (err) {
      setError('An unexpected error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await registerService({ username, email, password });
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoggedIn(true);
        return true;
      } else {
        setError(response.error || 'Registration failed');
        return false;
      }
    } catch (err) {
      setError('An unexpected error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    logoutService();
    setUser(null);
    setIsLoggedIn(false);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      isLoggedIn, 
      login, 
      register, 
      googleLogin,
      updateUser,
      logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};